/**
 * WorkingBillingClaimEditor
 *
 * Reuses SessionDetailsSection + BillingCodesSection from SessionBillingPopover
 * (the same editing UI providers use on the claim editing page and in note view)
 * to edit the working billing claim on an encounter.
 */

import React, { useCallback, useMemo } from 'react';
import {
  useDebouncedWorkingBillingClaim,
  useGetEncounterQuery,
  useUpdateEncounterMutation,
} from '@features/encounters';
import { BillingCodesSection } from '@features/encounters/components/SessionBillingPopover/BillingCodesSection';
import { SessionDetailsSection } from '@features/encounters/components/SessionBillingPopover/SessionDetailsSection';
import type { Modality } from '@features/encounters/types/attestations';
import {
  bareCodesToLineItems,
  buildDiagnosisCodeSources,
  extractBillingCodes,
  extractDiagnosisCodes,
  extractPlaceOfService,
} from '@features/encounters/utils/billingClaim';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import type { WorkingClaimLineItem } from '@store/generated/encounters';

interface WorkingBillingClaimEditorProps {
  encounterId: string;
  onSaved?: () => void;
}

export const WorkingBillingClaimEditor: React.FC<WorkingBillingClaimEditorProps> = ({
  encounterId,
  onSaved,
}) => {
  const { data: encounterData, isLoading } = useGetEncounterQuery(
    { encounterId },
    { skip: !encounterId },
  );

  const [updateEncounter] = useUpdateEncounterMutation();

  const { update: updateBillingClaim } = useDebouncedWorkingBillingClaim({
    encounterId,
    onSuccess: () => {
      onSaved?.();
    },
  });

  // Extract current values from encounter's working billing claim
  const cptCodes = useMemo(
    () => extractBillingCodes(encounterData?.working_billing_claim),
    [encounterData?.working_billing_claim],
  );

  const icd10Codes = useMemo(
    () => extractDiagnosisCodes(encounterData?.working_billing_claim),
    [encounterData?.working_billing_claim],
  );

  const placeOfService = useMemo(
    () => extractPlaceOfService(encounterData?.working_billing_claim),
    [encounterData?.working_billing_claim],
  );

  const modality = (encounterData?.modality as Modality | null) ?? null;

  const duration = useMemo(() => {
    if (encounterData?.start_time && encounterData?.end_time) {
      return Math.floor(
        (new Date(encounterData.end_time).getTime() -
          new Date(encounterData.start_time).getTime()) /
          (1000 * 60),
      );
    }
    return null;
  }, [encounterData?.start_time, encounterData?.end_time]);

  // Handlers — same pattern as ClaimLeftSidebarContent
  const handleModalityChange = useCallback(
    async (newModality: Modality) => {
      const pos = newModality === 'in_person' ? '11' : newModality === 'video' ? '10' : '02';
      await updateEncounter({
        encounterId,
        encounterUpdate: { modality: newModality },
      });
      updateBillingClaim({ placeOfService: pos });
      onSaved?.();
    },
    [encounterId, updateEncounter, updateBillingClaim, onSaved],
  );

  const handlePlaceOfServiceChange = useCallback(
    (pos: string) => {
      updateBillingClaim({ placeOfService: pos });
    },
    [updateBillingClaim],
  );

  const handleDurationChange = useCallback(
    async (minutes: number) => {
      const startTime = encounterData?.start_time;
      if (startTime) {
        const endTime = new Date(
          new Date(startTime).getTime() + minutes * 60 * 1000,
        ).toISOString();
        await updateEncounter({
          encounterId,
          encounterUpdate: { end_time: endTime },
        });
        onSaved?.();
      }
    },
    [encounterId, encounterData?.start_time, updateEncounter, onSaved],
  );

  const handleCptCodesChange = useCallback(
    (codes: string[]) => {
      const claim = encounterData?.working_billing_claim;
      updateBillingClaim({
        lineItems: bareCodesToLineItems(codes, claim?.line_items ?? undefined),
      });
    },
    [encounterData?.working_billing_claim, updateBillingClaim],
  );

  const handleLineItemsChange = useCallback(
    (items: WorkingClaimLineItem[]) => {
      updateBillingClaim({ lineItems: items });
    },
    [updateBillingClaim],
  );

  const handleIcd10CodesChange = useCallback(
    (codes: string[]) => {
      const filtered = codes.filter(c => c && c.trim() !== '');
      const claim = encounterData?.working_billing_claim;
      updateBillingClaim({
        diagnosisCodes: filtered,
        diagnosisCodeSources: buildDiagnosisCodeSources(
          filtered,
          claim?.diagnosis_code_sources,
        ),
      });
    },
    [encounterData?.working_billing_claim, updateBillingClaim],
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={80} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        {/* Session Details — modality, place of service, duration */}
        <Box>
          <Typography
            variant="overline"
            sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', mb: 1, display: 'block' }}
          >
            Session Details
          </Typography>
          <SessionDetailsSection
            modality={modality}
            onModalityChange={handleModalityChange}
            placeOfService={placeOfService}
            onPlaceOfServiceChange={handlePlaceOfServiceChange}
            duration={duration}
            onDurationChange={handleDurationChange}
            editable
          />
        </Box>

        {/* Billing Codes — CPT + ICD-10 */}
        <Box>
          <Typography
            variant="overline"
            sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary', mb: 1, display: 'block' }}
          >
            Billing Codes
          </Typography>
          <BillingCodesSection
            cptCodes={cptCodes}
            onCptCodesChange={handleCptCodesChange}
            cptLineItems={encounterData?.working_billing_claim?.line_items}
            onCptLineItemsChange={handleLineItemsChange}
            showModifiers
            claimDiagnosisCodes={icd10Codes}
            icd10Codes={icd10Codes}
            onIcd10CodesChange={handleIcd10CodesChange}
            diagnosisCodeSources={encounterData?.working_billing_claim?.diagnosis_code_sources}
            editable
          />
        </Box>
      </Stack>
    </Box>
  );
};
